'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle, ArrowLeft, Check, Clock, Crown, Lock, X as XIcon } from 'lucide-react';
import { LegalDocumentPage } from '@/features/support/ui/LegalDocumentPage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContentFullScreen,
  DrawerHeader as DrawerSheetHeader,
  DrawerTitle as DrawerSheetTitle,
} from '@/shared/ui/shadcn/drawer';
import { Button } from '@/shared/ui/shadcn/button';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { PaywallBullets } from '@/features/payment/ui/PaywallBullets';
import type { PaywallBullet } from '@/shared/lib/api';
import { formatPackPrice } from '@/shared/lib/format-catalog-price';
import { usePasses } from '@/shared/api/hooks/use-passes';
import {
  usePassPaywallStore,
  type PassPaywallHighlight,
} from '@/shared/stores/pass-paywall-store';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import type { Pass, PassPaywallCopy, PassSubscriptionState } from '@/types/passes';
import {
  AnalyticsEvent,
  PackTrigger,
  PaywallSurface,
  trackEvent,
} from '@/shared/lib/analytics';
import { PassBuyCta } from './PassBuyCta';
import { ManageSubscriptionSection } from './ManageSubscriptionSection';

/**
 * Map a paywall highlight to the Pass `entitlement_key`. These keys are the
 * server's stable identifiers (mirrors Apple's `Highlight` rawValues:
 * pro → pro_pass, pro_plan → pro_plan). The opaque keys are never composed on
 * the client beyond this lookup.
 *
 * Both highlights open this sheet: GET /passes serves the sellable catalog
 * (Tokens Pro Pass / Pro Plan), so each highlight resolves its primaryPass here.
 */
const ENTITLEMENT_KEY: Record<PassPaywallHighlight, string> = {
  pro: 'pro_pass',
  pro_plan: 'pro_plan',
};

/**
 * Root-mounted Pass paywall — the upsell for locked Pro render-settings
 * features. Mirrors Apple's `PassPaywallSheet`: hero placeholder, server
 * tagline/subtitle/feature checklist, single primary CTA, restore + terms
 * footer.
 *
 * All display copy is server-driven via `GET /v1/passes` (principle 5) — the
 * client never composes Pass descriptions locally. Purchase runs in-page through
 * the Paddle web checkout ({@link PassBuyCta} off the server `checkout`
 * descriptor); the webhook owns the grant — the client never fabricates a
 * purchase or entitlement (money path: no fallbacks/fakes).
 */
export function PassPaywallDialog() {
  const t = useTranslations('passes');
  const tCommon = useTranslations('common');

  const highlight = usePassPaywallStore((s) => s.highlight);
  const returnTo = usePassPaywallStore((s) => s.returnTo);
  const close = usePassPaywallStore((s) => s.close);
  const showTokenPaywall = useLimitPaywallStore((s) => s.showPaywall);

  // Back to the token-paywall dialog this sheet was opened from (the "Pro
  // Plan" alternative-action card in LimitPaywallDialog). Closing this store
  // BEFORE reopening the token one avoids two modal Dialogs mounted open at
  // once — each renders its own translucent overlay, and stacking them
  // visibly doubles the dim (see LimitPaywallDialog for the full rationale).
  const handleBack = () => {
    const target = returnTo;
    close();
    if (target) {
      showTokenPaywall(target.payload, target.code, target.meta ?? undefined);
    }
  };

  // While the Paddle checkout overlay is up we relax this dialog to non-modal.
  // A modal Radix Dialog locks `<body>` (`react-remove-scroll` →
  // `pointer-events: none` + focus trap); Paddle injects its overlay iframe at
  // `<body>` level, OUTSIDE this dialog's subtree, so under the lock it renders
  // but is unclickable. Relaxing the lock (rather than closing the dialog) keeps
  // the paywall mounted so it flips to the owned celebration in place on
  // completion (parity with the storefront + Apple/Android). Restored on close.
  const [checkoutActive, setCheckoutActive] = useState(false);

  // True once a purchase completed IN THIS DIALOG SESSION (Paddle checkout
  // `onPurchased`). Terminal state: the back chevron to the parent token
  // paywall is suppressed (that sheet is a stale pre-purchase snapshot where
  // the product still looks buyable) and a Done button under the celebration
  // closes the whole stack — mirrors PackPaywallContent's `didPurchase`.
  // NOT set when the dialog opens already-owned. Reset on close.
  const [didPurchase, setDidPurchase] = useState(false);

  // Gate the (auth-only) catalog fetch on the dialog actually being open — the
  // dialog is root-mounted, so an ungated query would 401 on pre-auth pages.
  const { data: passes = [], isLoading, isError, refetch } = usePasses({
    enabled: highlight !== null,
  });

  // Funnel: fire the Pro Plan paywall-shown event when the pro_plan tier
  // opens (the contractor funnel). The Pro Pass is tracked through its own
  // store surface, so it doesn't emit the pro_plan event.
  useEffect(() => {
    if (highlight !== 'pro_plan') return;
    trackEvent(AnalyticsEvent.PACK_PAYWALL_SHOWN, {
      surface: PaywallSurface.PRO_PLAN,
      trigger: PackTrigger.PRO_PLAN,
      project_bound: false,
    });
  }, [highlight]);

  const primaryPass = useMemo<Pass | undefined>(() => {
    if (!highlight) return undefined;
    return passes.find(
      (p) => p.attributes.entitlement_key === ENTITLEMENT_KEY[highlight]
    );
  }, [passes, highlight]);

  return (
    <Drawer
      open={highlight !== null}
      // Relaxed to non-modal only while the Paddle overlay is up, so its
      // body-level iframe stays clickable (see `checkoutActive`).
      modal={!checkoutActive}
      onOpenChange={(open) => {
        if (!open) {
          setCheckoutActive(false);
          setDidPurchase(false);
          close();
        }
      }}
    >
      {/* `bg-panel` (iOS system-grouped-background gray) — matches the
          Apple/Android Pass paywall sheet background instead of pure white. */}
      <DrawerContentFullScreen className="bg-panel">
        <DrawerClose
          aria-label={tCommon('close')}
          className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <XIcon className="h-4 w-4" />
          <span className="sr-only">{tCommon('close')}</span>
        </DrawerClose>
        {/* Back to the token paywall this sheet was reached from — only
            present when opened via the "Pro Plan" alternative-action card
            (see `handleBack`). Absent for every other entry point (locked
            render-setting taps, storefront), which have no parent sheet. */}
        {/* Suppressed after a purchase in this session: the parent sheet is a
            stale pre-purchase snapshot — Done under the celebration is the
            single terminal exit (Pack parity). */}
        {returnTo && !didPurchase && (
          <button
            type="button"
            onClick={handleBack}
            aria-label={tCommon('back')}
            className="absolute left-4 top-4 z-10 rounded-full p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        {/* pt-12 clears the absolutely-positioned close (top-right) and back
            (top-left) buttons: the centered full-width hero headline would
            otherwise start at the same y and run under the close X. */}
        <div className="flex h-full flex-col overflow-y-auto overscroll-contain px-4 pt-12 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-6">
          <DrawerSheetHeader className="sr-only px-0">
            <DrawerSheetTitle>{t('sectionTitle')}</DrawerSheetTitle>
          </DrawerSheetHeader>

          {isLoading ? (
            <div className="py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : isError || !primaryPass ? (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <p className="text-card-body text-muted-foreground">
                {t('loadError')}
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                {tCommon('tryAgain')}
              </Button>
            </div>
          ) : (
            <PaywallBody
              primary={primaryPass}
              onCheckoutActiveChange={setCheckoutActive}
              onPurchased={() => setDidPurchase(true)}
              onDone={didPurchase ? close : undefined}
            />
          )}
        </div>
      </DrawerContentFullScreen>
    </Drawer>
  );
}

function PaywallBody({
  primary,
  onCheckoutActiveChange,
  onPurchased,
  onDone,
}: {
  primary: Pass;
  /** Relax/restore the host dialog's modal body-lock around the Paddle overlay. */
  onCheckoutActiveChange: (active: boolean) => void;
  /** Paddle checkout completed — flips the host's terminal `didPurchase`. */
  onPurchased: () => void;
  /** Set only right after a purchase in this session: renders the terminal
   *  Done CTA under the celebration that closes the whole paywall stack
   *  (Pack parity). Undefined when the dialog opened already-owned. */
  onDone?: () => void;
}) {
  const t = useTranslations('passes');
  const tPacks = useTranslations('packs');
  const tCheckout = useTranslations('subscriptionCheckout');
  const { paywall, is_active } = primary.attributes;

  const isProPlan =
    primary.attributes.entitlement_key === ENTITLEMENT_KEY.pro_plan;

  // Real catalog price flushed top-right of the offer card, mirroring the Facade
  // Pack card. Same source/format the storefront PassCard + the LimitPaywall
  // alternative card use (never invented locally). Pro Plan is recurring, so it
  // carries a "/month" suffix; the lifetime Pro Pass shows the bare one-time price.
  const priceLabel = isProPlan
    ? `${formatPackPrice(primary.attributes)}${tCheckout('pricingSummary.perMonth')}`
    : formatPackPrice(primary.attributes);
  // In-app Terms — shown in a nested dialog OVER the paywall, never a web
  // hand-off (keeps the user next to the CTA). Reuses the same LegalDocumentPage
  // the Account screen renders.
  const [showTerms, setShowTerms] = useState(false);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Owned → celebration (the buy already happened — entitlements follow the
          user_id, so a Pass bought on mobile shows owned here too); not owned →
          the purchase funnel. */}
      {is_active ? (
        <>
          <OwnedCelebration
            paywall={paywall}
            statusActiveLabel={t('statusActive')}
            subscription={primary.attributes.subscription ?? null}
          />
          {/* Single, terminal exit right after a purchase in this session:
              closes the whole paywall stack so the buyer starts working.
              Returning to the parent paywall (which still offers the
              just-bought product) would be a dead end — hence Done, and the
              back chevron suppressed in the host chrome. Pack parity. */}
          {onDone && (
            <Button className="w-full" onClick={onDone}>
              {tPacks('proPlanDone')}
            </Button>
          )}
        </>
      ) : (
        <>
          {/* Hero headline ABOVE the offer card. Per-pass server copy ("Render
              like a pro — forever" for the Pro Pass) — carries the lifetime
              promise where it gets the most attention, before any feature
              bullet. Centered to balance the card below. Mirrors
              Apple / Android. */}
          <h2 className="text-screen-subtitle text-foreground w-full text-center font-bold">
            {paywall.headline}
          </h2>

          {/* Unified product-card chrome shared 1:1 with the Facade Pack offer
              card: clean white surface (`bg-product-card`), borderless, lifted by
              a soft `shadow-xl`, `rounded-2xl` — reads as a prepared deliverable,
              premium and floating. Header = product name top-left + real price
              flushed top-right (both `screen-subtitle` bold), billing-model note
              as the meta line beneath, then the feature bullets — the exact same
              structure and tokens as the Pack card. */}
          <div className="flex w-full flex-col gap-5 rounded-2xl bg-product-card p-4 shadow-xl">
            <div className="flex w-full flex-col gap-1">
              {/* `flex-wrap` so name + price sit side-by-side when they fit and
                  the price WRAPS onto its own line when a long currency won't —
                  the price must never ellipsize (App Store rejects a hidden
                  price). Mirrors the Pack offer card. */}
              <div className="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-1">
                <h2 className="min-w-0 text-screen-subtitle text-foreground font-bold">
                  {paywall.tagline}
                </h2>
                <p className="text-screen-subtitle text-foreground font-bold">
                  {priceLabel}
                </p>
              </div>
              {/* Billing-model note (e.g. "Pay once — no subscription") as the
                  meta line directly under name + price — parity with the Pack
                  card's `body` line. */}
              {paywall.billing_note ? (
                <p className="text-card-meta text-muted-foreground">
                  {paywall.billing_note}
                </p>
              ) : null}
            </div>

            <PaywallBullets bullets={featuresToBullets(paywall.features)} />
          </div>

          {/* Real in-page purchase: the Paddle web checkout opened off the
              server `checkout` descriptor (parity with the storefront PassCard
              pill). Country not served on a web rail (RU/BY) → the server
              `web_payment_notice` banner instead of a dead button; never App
              Store / Google Play badges. The webhook owns the grant — on
              completion `['passes']` refetches, `is_active` flips, and this body
              re-renders into the owned celebration above. The reference price
              stays visible via `price_note` at the top of the offer card. */}
          <PassBuyCta
            descriptor={primary.attributes.checkout}
            kind={isProPlan ? 'subscription' : 'pass'}
            sku={
              primary.attributes.provider_google_product_id ??
              primary.attributes.provider_apple_product_id ??
              primary.id
            }
            // Price-free verb label ("Subscribe" / "Get Pro Pass") — the price
            // now shows as its own flushed-right value in the offer card header,
            // so the button stays a clean action and never repeats it (parity
            // with the Facade Pack CTA). Falls back to the price-baked `cta_label`
            // only for older payloads without the plain field.
            verbLabel={paywall.cta_label_plain ?? paywall.cta_label}
            webPaymentNotice={primary.attributes.web_payment_notice}
            onStart={() => {
              onCheckoutActiveChange(true);
              // Funnel: the web "subscribe started" analog for the Pro Plan.
              if (isProPlan) {
                trackEvent(AnalyticsEvent.PRO_PLAN_SUBSCRIBE_STARTED);
              }
            }}
            onPurchased={onPurchased}
            onClosed={() => onCheckoutActiveChange(false)}
          />

          {/* Trust microcopy — web checks out via Paddle, so it reads the
              paddle twin of the secure-payment line (Apple → App Store,
              Android → Google Play). Server-driven; rendered only when
              non-empty. */}
          {paywall.secure_payment_paddle ? (
            <p className="text-card-meta text-muted-foreground flex items-center justify-center gap-1">
              <Lock className="h-3 w-3" aria-hidden="true" />
              {paywall.secure_payment_paddle}
            </p>
          ) : null}
        </>
      )}

      {/* Expectation-setting fine print — a Pass unlocks features/limits,
          generation still spends tokens. `text-muted-foreground` matches the
          surrounding grey labels (Restore · Terms below) so it reads at the
          same contrast — the previous `/70` opacity was too washed-out to
          actually be read. Hidden once the Pass is owned: the celebration is
          a reward screen, not a purchase funnel, so the expectation-setting
          disclaimer reads as a sour note there. */}
      {!is_active && paywall.token_note && (
        <p className="text-center text-card-meta text-muted-foreground">
          {paywall.token_note}
        </p>
      )}

      {/* Terms footer. No "Restore purchases" on web: that's an app-store
          concept (StoreKit / Play Billing re-sync device-local receipts). With
          Paddle (Merchant of Record) entitlements are account-bound server-side
          and already present on login, then kept fresh by the WS `user/updated`
          reconcile — there is nothing to restore, so the inert label (it never
          did anything, it was a plain span) is dropped rather than faked. The
          server still ships `restore_label` for the mobile sheets. Terms opens
          the in-app legal document in a nested dialog over the paywall. */}
      <div className="flex items-center gap-3 text-card-meta text-muted-foreground">
        <button
          type="button"
          onClick={() => setShowTerms(true)}
          className="transition-colors hover:text-foreground"
        >
          {paywall.terms_label}
        </button>
      </div>

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent
          showCloseButton
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{paywall.terms_label}</DialogTitle>
          </DialogHeader>
          <LegalDocumentPage type="terms" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Map the server `features` (plain strings) to the shared `PaywallBullets`
 * shape. They carry no semantic icon, so each gets the default `check` glyph —
 * the Pass paywall and the Facade Pack offer card then render the exact same
 * bullet component (20px teal icon, `gap-3`, `text-list-item-subtitle`).
 */
function featuresToBullets(features: string[]): PaywallBullet[] {
  return features.map((label) => ({ icon: 'check', label }));
}

/** Post-purchase celebration shown once the Pass is owned. Mirrors the
 *  owned-Pass card on the Tokens page (`PassRow`): a crown, a glowing teal frame
 *  + faint tint, and the filled "Active" capsule — so the owner feels the
 *  upgrade actually landed instead of reading a greyed-out "Active" button. Copy
 *  is server-driven (`owned_headline` / `owned_note`); the badge label stays
 *  i18n-owned (`statusActive`), matching the Tokens page. */
function OwnedCelebration({
  paywall,
  statusActiveLabel,
  subscription,
}: {
  paywall: PassPaywallCopy;
  statusActiveLabel: string;
  subscription: PassSubscriptionState | null;
}) {
  // Same unified product-card chrome as the purchase hero and the Facade Pack
  // card: clean white surface, borderless, soft `shadow-xl`, `rounded-2xl`.
  const frameClass =
    'flex w-full flex-col items-center gap-3 rounded-2xl bg-product-card p-4 text-center shadow-xl';
  const crownClass = 'h-10 w-10 text-primary';

  // Status chip reflects the auto-renewable lifecycle: healthy → tier accent +
  // check; pending cancellation → amber clock; grace/billing issue → warning
  // triangle. Label is server-driven (`status_label`) for the Pro Plan,
  // falling back to the local "Active" for lifetime Passes (no lifecycle).
  const attention =
    subscription?.is_in_grace_period === true ||
    subscription?.is_pending_cancellation === true;
  const StatusIcon = subscription?.is_in_grace_period
    ? AlertTriangle
    : subscription?.is_pending_cancellation
      ? Clock
      : Check;
  const badgeClass = attention
    ? 'text-card-meta text-warning inline-flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1'
    : 'text-card-meta text-primary inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1';
  return (
    <div className={frameClass}>
      <Crown className={crownClass} />
      <h2 className="text-card-title text-foreground">{paywall.owned_headline}</h2>
      <span className={badgeClass}>
        <StatusIcon className="h-3.5 w-3.5" />
        {paywall.status_label ?? statusActiveLabel}
      </span>
      <p className="text-card-body text-muted-foreground">{paywall.owned_note}</p>
      {/* `text-left` resets the card's `text-center` so wrapped bullet lines
          stay left-flagged (icons in one column). */}
      <PaywallBullets
        bullets={featuresToBullets(paywall.features)}
        className="text-left"
      />
      {/* Manage affordance + purchase-channel clarifier (Pro Plan only —
          lifetime Passes ship no `subscription`, so this renders nothing).
          Server copy verbatim; provider routes paddle → fresh portal URL,
          apple/google → the store's subscriptions page. */}
      <ManageSubscriptionSection
        provider={subscription?.provider}
        manageLabel={paywall.manage_label}
        purchasedViaLabel={paywall.purchased_via_label}
        multipleSubscriptionsNotice={paywall.multiple_subscriptions_notice}
      />
    </div>
  );
}
